/**
 * Sign in / Create account (docs/11 §3.2): two labelled fields, show/hide
 * password, inline error under the fields. Sign-up adds a name and the 18+
 * confirmation and goes straight to Tonight; the profile questions come later.
 */
import { useState } from 'react';
import { ChevronLeft, Eye, EyeOff } from 'lucide-react';
import { api, ApiError } from './api.js';
import { Button, InlineWarn } from './ui.js';
import { SocialButtons } from './SocialButtons.js';

export function AuthScreen({ mode: initialMode, onSignedIn, onBack }: {
  mode: 'login' | 'register' | 'forgot';
  onSignedIn: () => void;
  onBack: () => void;
}) {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>(initialMode);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [adult, setAdult] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailOk = /.+@.+\..+/.test(email);
  const canLogin = emailOk && password.length >= 1;
  const canRegister = emailOk && password.length >= 8 && name.trim().length >= 2 && adult;

  async function submit() {
    setBusy(true); setError(null);
    try {
      if (mode === 'forgot') {
        await api.forgotPassword(email);
        setSent(true); setBusy(false);
        return;
      }
      if (mode === 'login') await api.login(email, password);
      else await api.register({ email, password, display_name: name.trim(), adult: true });
      onSignedIn();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) { setError('That email already has an account — sign in instead.'); setMode('login'); }
      else if (err instanceof ApiError && err.status === 401) setError('Email or password is incorrect.');
      else if (err instanceof ApiError && err.status === 429) setError('Too many attempts. Wait a few minutes and try again.');
      else if (err instanceof ApiError && err.status === 400 && mode === 'forgot') setError('Enter a valid email address.');
      else if (err instanceof ApiError && err.status === 400) setError(mode === 'register' ? 'Use a valid email and a password of 8+ characters.' : 'Check your email and password.');
      else setError('Couldn’t reach Surge — check your signal.');
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <header className="hdr">
        <button className="back" onClick={onBack}><ChevronLeft size={24} aria-hidden="true" /> Back</button>
      </header>
      <h1 className="title">{mode === 'login' ? 'Sign in' : mode === 'forgot' ? 'Reset your password' : 'Create your account'}</h1>
      {mode === 'forgot' && !sent && (
        <p className="sub">Enter the email you signed up with and we’ll send a link to set a new password.</p>
      )}
      {mode === 'forgot' && sent && (
        <>
          <p className="sub">
            If <b style={{ color: 'var(--text-1)' }}>{email}</b> has a Surge account, a link is on its way.
            It works once and expires in an hour. Check your spam folder if it doesn’t arrive.
          </p>
          <Button variant="primary" onClick={() => { setMode('login'); setSent(false); setError(null); }}>Back to sign in</Button>
        </>
      )}
      {!(mode === 'forgot' && sent) && (<>
      {mode !== 'forgot' && <SocialButtons />}

      <div className="field">
        <label htmlFor="email">Email</label>
        <input id="email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none"
          placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      {mode !== 'forgot' && (
      <div className="field">
        <label htmlFor="password">{mode === 'login' ? 'Password' : 'Password (8+ characters)'}</label>
        <div className="input-wrap">
          <input id="password" className="has-trailing" type={show ? 'text' : 'password'}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            value={password} onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && mode === 'login' && canLogin) void submit(); }} />
          <button type="button" className="trailing" aria-label={show ? 'Hide password' : 'Show password'}
            aria-pressed={show} onClick={() => setShow((s) => !s)}>
            {show ? <EyeOff size={22} aria-hidden="true" /> : <Eye size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>
      )}

      {mode === 'register' && (
        <>
          <div className="field">
            <label htmlFor="name">Your name</label>
            <input id="name" type="text" autoComplete="name" value={name} maxLength={50}
              onChange={(e) => setName(e.target.value)} />
          </div>
          <label className="check">
            <input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} />
            <span>I’m 18 or older. Surge keeps my details to itself — never sold, never shared. <a href="/privacy" target="_blank" rel="noopener">Privacy policy</a></span>
          </label>
        </>
      )}

      {error && <InlineWarn danger>{error}</InlineWarn>}

      <Button variant="primary" busy={busy}
        disabled={mode === 'login' ? !canLogin : mode === 'forgot' ? !emailOk : !canRegister}
        onClick={() => void submit()}>
        {mode === 'login' ? 'Sign in' : mode === 'forgot' ? 'Send reset link' : 'Create account'}
      </Button>
      {mode === 'login' && (
        <Button variant="text" onClick={() => { setMode('forgot'); setError(null); }}>Forgot your password?</Button>
      )}
      <Button variant="text" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(null); }}>
        {mode === 'login' ? 'New here? Create an account' : mode === 'forgot' ? 'Back to sign in' : 'Have an account? Sign in'}
      </Button>
      </>)}
    </div>
  );
}
